import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import {
  useDeleteAccountMutation,
  useMergeAccountsMutation,
  useUpdateAccountMutation,
  UpdateAccountInput,
  AccountPropertiesInput,
  GetAccountDocument,
} from '@/lib/graphql/types-and-hooks';
import { buildBatchDeleteAccountsMutation } from '@/lib/graphql/batch';
import { isLovablePreview } from '@/lib/utils/environment';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { toast } from 'sonner';
import { useCanMutate, assertCanMutate } from '@/lib/rbac';
import { mergeFailureMessage } from '@/lib/graphql/merge-refusal';

export interface UpdateAccountParams {
  id: string;
  properties?: Partial<AccountPropertiesInput>;
  customProperties?: Record<string, any>;
}

export const useCompanyMutations = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const workspaceId = useWorkspaceId();
  const canMutate = useCanMutate();
  const [deleteAccountMutation, { loading: deleting }] = useDeleteAccountMutation();
  const [mergeAccountsMutation, { loading: merging }] = useMergeAccountsMutation();
  const [updateAccountMutation, { loading: updating }] = useUpdateAccountMutation();

  const deleteAccount = useCallback(
    async (id: string) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        // ⚠️ CRITICAL: Don't use extractPlainId() on entity IDs (Pattern 29, Section 4)
        await deleteAccountMutation({
          variables: { id }, // Use ID as-is from GraphQL
        });

        toast.success(t('mutations.companyDeleted'));
        return { success: true };
      } catch (error) {
        console.error('Failed to delete company:', error);
        toast.error(t('mutations.companyDeleteFailed'));
        return { success: false, error };
      }
    },
    [deleteAccountMutation, canMutate, t]
  );

  // Batch delete — single request with one aliased deleteAccount per id, and a
  // single "N companies deleted" toast (bulk delete previously fired one toast
  // per row). Mirrors deleteDeals in use-deal-mutations.
  const deleteAccounts = useCallback(
    async (ids: string[]) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (ids.length === 0) return { success: true };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        const result = await client.mutate({
          mutation: buildBatchDeleteAccountsMutation(ids), // ids used as-is (Pattern 29, Section 4)
          update: (cache) => {
            // Evict deleted accounts so cached lists (detail sheets, contact
            // associations) drop them reactively without a refetch.
            ids.forEach((id) => {
              cache.evict({ id: cache.identify({ __typename: 'Account', id }) });
            });
            cache.gc();
          },
        });

        // Each alias returns { success }; a falsy success means the backend
        // rejected that delete even though the request itself didn't throw.
        const data = (result.data ?? {}) as Record<string, { success?: boolean } | null>;
        const failed = ids.filter((_, i) => !data[`delete_${i}`]?.success);
        if (failed.length > 0) {
          throw new Error(`Backend failed to delete ${failed.length} of ${ids.length} companies`);
        }

        toast.success(t('mutations.companiesDeleted', { count: ids.length }));
        return { success: true };
      } catch (error) {
        console.error('Failed to delete companies:', error);
        toast.error(t('mutations.companiesDeleteFailed'));
        return { success: false, error };
      }
    },
    [client, canMutate, t]
  );

  const mergeAccounts = useCallback(
    async (primaryAccountId: string, otherAccountIds: string[]) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        // ⚠️ CRITICAL: Don't use extractPlainId() on entity IDs
        const result = await mergeAccountsMutation({
          variables: {
            args: {
              primaryAccountId,
              otherAccountIds,
            },
          },
          // Evict secondaries from the normalized cache so they drop out of
          // accounts.edges and contact.accounts.edges immediately. We can't
          // rely on the mutation's returned primaryAccount for auto-normalization
          // because the backend returns it with a postgres-int id in DDB-mode
          // workspaces, which doesn't match the DDB-keyed cache entry the list
          // points at — see the refetch below.
          update: (cache, { data }) => {
            if (!data?.mergeAccounts?.success) return;
            otherAccountIds.forEach(id => {
              cache.evict({ id: cache.identify({ __typename: 'Account', id }) });
            });
            cache.gc();
          },
        });
        if (!result.data?.mergeAccounts?.success) throw new Error(t('mutations.companiesMergeFailed'));
      } catch (error) {
        console.error('Failed to merge companies:', error);
        toast.error(mergeFailureMessage(error, t('mutations.companiesMergeFailed')));
        return { success: false, error };
      }

      // The merge has happened; a failed refresh must not report it as failed.
      try {
        // Refetch the merged primary by its DDB id through the singular
        // resolver. This doesn't go through OpenSearch (no consistency race)
        // and writes the merged data back into the DDB-keyed cache entry the
        // list row references, so the list updates without a full refetch.
        // resolve_account in coffee dereferences filter.workspace_id without a
        // null check, so the filter arg is required.
        await client.query({
          query: GetAccountDocument,
          variables: {
            id: primaryAccountId,
            filter: { workspaceId: String(workspaceId) },
          },
          fetchPolicy: 'network-only',
        });
      } catch (error) {
        console.error('Companies merged, but refreshing the primary failed:', error);
      }

      toast.success(t('mutations.companiesMerged'));
      return { success: true };
    },
    [mergeAccountsMutation, client, workspaceId, canMutate, t]
  );

  const updateAccount = useCallback(
    async (params: UpdateAccountParams) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        const input: UpdateAccountInput = {
          id: params.id, // Use ID as-is
          properties: params.properties || {}, // Use empty object instead of null to avoid backend NoneType errors
          customProperties: params.customProperties,
        };

        const result = await updateAccountMutation({
          variables: { input },
          // No refetchQueries — the mutation returns full AccountFields which updates
          // the Apollo normalized cache. Inline edits use cellEdits local state for
          // immediate UI feedback. Refetching getAccounts would reset pagination.
        });

        if (result.data?.updateAccount?.account) {
          // Shared id with useAccountUpdate: a detail-sheet edit can persist through
          // both hooks (sheet updater + grid debounced save), so the second toast
          // replaces the first instead of stacking a duplicate.
          toast.success(t('mutations.companyUpdated'), { id: `company-update-${params.id}` });
          return { success: true, data: result.data.updateAccount.account };
        }

        throw new Error(t('mutations.companyUpdateFailed'));
      } catch (error) {
        console.error('Failed to update company:', error);
        toast.error(error instanceof Error ? error.message : t('mutations.companyUpdateFailed'), {
          id: `company-update-${params.id}`,
        });
        return { success: false, error };
      }
    },
    [updateAccountMutation, canMutate, t]
  );

  return {
    deleteAccount,
    deleteAccounts,
    mergeAccounts,
    updateAccount,
    deleting,
    merging,
    updating,
  };
};
